import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from '../shared/Button';
import EmptyState from '../shared/EmptyState';
import ImageScrim from '../shared/ImageScrim';
import PhotoCredit from '../shared/PhotoCredit';
import SeeAllLink from '../shared/SeeAllLink';
import Shimmer from '../shared/Shimmer';
import HomeSectionHeader from './HomeSectionHeader';
import { isDismissed } from '../../utils/composeWhatsOnSections';
import { bucketByWeek } from '../../utils/whatsOnBuckets';
import { distanceKm } from '../../utils/tripAwareLabels';
import { FALLBACK_LOCAL_RADIUS_KM } from '../../config/whatsOnDiscovery';
import { autoNormalizeCardData } from '../../utils/normalizeCardData';
import { sanitizeUrl } from '../../utils/sanitizeUrl';
import { resizeForSurface } from '../../utils/imageSource';
import { buildPhotoSrcSet } from '../../utils/photoSrcSet';
import { formatTime, getLocalDateString, getQuickDateRange, parseLocalDate, getDisplayLocale } from '../../utils/dateUtils';

const ROW_LIMIT = 4;
// From Friday the page says "This weekend"; before that, "This week" — the
// strip's two-week window holds both, and a Tuesday page titled "weekend"
// over Tuesday's events would be a small lie.
const WEEKEND_FROM_DAY = 5;

const itemDate = (item) => (item.event_date || item.start_date || item.date || '').slice(0, 10);
const itemPhoto = (item) => item.cover_photo || item.image_url || item.imageUrl || null;

/**
 * What the section (and the no-trip hero) shows from the What's On teaser:
 * the items the viewer hasn't dismissed, cut to the bucket the title names.
 * The teaser looks two weeks out and holds this weekend AND next; a page
 * titled "This weekend" must not list next Saturday. `scope` says which
 * bucket survived: 'soon' (now to Sunday), 'next' (next weekend, when
 * nothing is on sooner) or 'all' (the global, unfenced list).
 *
 * `awayCenter` is the phone's centre when it is away from home. The filler
 * is fenced there, but follow pins are not (a venue follow has no fence; an
 * artist's is around home), so a followed show back home can be in a list
 * titled "This weekend in Barcelona". It is demoted, never dropped (Kurtis,
 * 2026-09-10 — the trip can change and the show may still matter): it goes
 * below everything around the phone and is marked `_awayFromCenter`, so its
 * line names its city. The week is chosen by what is around the phone too: a
 * home show alone this week must not hide next weekend's local list — it
 * comes along under it.
 */
export function weekendSlice(items, dismissedIds, todayIso, fenced = true, awayCenter = null) {
  const visible = dismissedIds?.size > 0 ? (items || []).filter(item => !isDismissed(item, dismissedIds)) : (items || []);
  if (!fenced) return { items: visible, scope: 'all' };
  const buckets = bucketByWeek(visible, todayIso);
  const soon = buckets.soon.items;
  const next = buckets.nextWeekend.items;
  if (awayCenter) {
    const hasNear = (list) => list.some(item => !isFarFromCenter(item, awayCenter));
    const scoped = hasNear(soon) ? { items: soon, scope: 'soon' }
      : hasNear(next) ? { items: [...soon, ...next], scope: 'next' }
        : soon.length > 0 ? { items: soon, scope: 'soon' }
          : next.length > 0 ? { items: next, scope: 'next' }
            : { items: visible, scope: 'all' };
    return { ...scoped, items: sinkFarFromCenter(scoped.items, awayCenter) };
  }
  if (soon.length > 0) return { items: soon, scope: 'soon' };
  if (next.length > 0) return { items: next, scope: 'next' };
  return { items: visible, scope: 'all' };
}

/** Outside the fence around `center` (the filler's own radius). No coordinates is not far. */
export function isFarFromCenter(item, center) {
  const km = distanceKm(center?.lat, center?.lng, item.latitude ?? item.lat, item.longitude ?? item.lng);
  return km != null && km > FALLBACK_LOCAL_RADIUS_KM;
}

/** Items outside the fence around `center` moved below the rest, in their order, and marked. */
export function sinkFarFromCenter(items, center) {
  const near = [];
  const far = [];
  for (const item of items) {
    if (isFarFromCenter(item, center)) far.push({ ...item, _awayFromCenter: true });
    else near.push(item);
  }
  return far.length ? [...near, ...far] : items;
}

/** What the section puts on screen of a slice: the lead card (the first event) and up to ROW_LIMIT rows. */
export function weekendShown(visible) {
  const lead = visible.find(i => i.content_type === 'event' || i.content_type === 'event_group') || visible[0] || null;
  const rows = visible.filter(i => i !== lead).slice(0, ROW_LIMIT);
  return { lead, rows };
}

/** A card's tap target: the in-app route, or a new tab for an off-site ticket page. */
function DetailLink({ normalized, className, children, ...rest }) {
  if (!normalized.detailUrl) return <div className={className}>{children}</div>;
  if (normalized.externalLink) {
    return <a href={sanitizeUrl(normalized.detailUrl)} target="_blank" rel="noopener noreferrer" className={className} {...rest}>{children}</a>;
  }
  return <Link to={normalized.detailUrl} className={className} {...rest}>{children}</Link>;
}

/** "Tonight" / "Tomorrow" / "Sat 8 Nov" for an event's day. */
function useDayLabel() {
  const { t } = useTranslation('home');
  const today = getLocalDateString(new Date());
  return (item) => {
    const date = itemDate(item);
    if (!date) return null;
    if (date === today) {
      const hour = Number(String(item.event_time || '').slice(0, 2));
      return hour >= 17 ? t('weekend.tonight') : t('weekend.today');
    }
    const tomorrow = getLocalDateString(new Date(Date.now() + 86400000));
    if (date === tomorrow) return t('weekend.tomorrow');
    return parseLocalDate(date).toLocaleDateString(getDisplayLocale(), { weekday: 'short', day: 'numeric', month: 'short' });
  };
}

function metaLine(item, t) {
  const time = item.event_time ? formatTime(item.event_time) : null;
  const venue = item.venue_name || item.venue || item.location || item.city || null;
  const price = item.is_free ? t('weekend.free') : (item.price_display || null);
  // A show demoted for being far from where the phone is says where it is.
  const city = item._awayFromCenter && item.city && item.city !== venue ? item.city : null;
  return [time, venue, city, price].filter(Boolean).join(t('hero.separator'));
}

function LeadCard({ item, buildActions, dayLabel }) {
  const { t } = useTranslation('home');
  const normalized = autoNormalizeCardData(item);
  const photo = itemPhoto(item);
  const sized = photo ? resizeForSurface(photo, 'large') : null;
  const srcSet = photo ? buildPhotoSrcSet(photo) : null;
  const actions = buildActions(item);
  const ticketUrl = normalized.typeData?.ticketUrl || null;
  const isBooking = normalized.typeData?.hasTicketUrl;

  return (
    <article className="relative bg-surface rounded-card border border-border overflow-hidden flex flex-col">
      <div className="relative aspect-[4/3] md:aspect-[2/1] bg-background overflow-hidden">
        {photo && (
          <img src={sized} srcSet={srcSet || undefined} sizes={srcSet ? '(max-width: 768px) 100vw, 740px' : undefined} alt="" className="w-full h-full object-cover" loading="lazy" />
        )}
        <ImageScrim className="h-3/4 from-black/80 via-black/30" />
        <div className="absolute left-4 right-4 bottom-4 text-white flex flex-col gap-1.5 z-raised pointer-events-none">
          <div className="flex gap-2">
            {dayLabel(item) && <span className="px-2.5 h-6 inline-flex items-center rounded-full bg-coral text-coral-ink text-xs font-bold">{dayLabel(item)}</span>}
            {normalized.typeData?.categoryLabel && <span className="px-2 h-6 inline-flex items-center rounded-full bg-white/20 text-white text-xs font-semibold">{normalized.typeData.categoryLabel}</span>}
          </div>
          <h3 className="font-display font-medium text-2xl md:text-3xl leading-tight [text-wrap:balance]">{normalized.title}</h3>
          <p className="text-sm text-white/90">{metaLine(item, t)}</p>
        </div>
        {photo && <PhotoCredit src={sized} size="md" />}
        {normalized.detailUrl && (
          <DetailLink normalized={normalized} className="absolute inset-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-light" aria-label={t('weekend.openEvent', { title: normalized.title })} />
        )}
      </div>
      <div className="flex items-center gap-2 p-3 md:px-4">
        <p className="flex-1 text-sm text-content-secondary line-clamp-2">
          {item._recommendation?.origin === 'followed_artist' || item._recommendation?.origin === 'followed_venue'
            ? t('weekend.whyFollow')
            : t('weekend.whyRanked')}
        </p>
        {actions.onSave && <Button variant="secondary" size="sm" onClick={actions.onSave}>{t('weekend.save')}</Button>}
        {ticketUrl ? (
          <Button size="sm" href={ticketUrl} target="_blank" rel="noopener noreferrer">{isBooking ? t('weekend.tickets') : t('weekend.details')}</Button>
        ) : normalized.detailUrl ? (
          <Button size="sm" to={normalized.detailUrl}>{t('weekend.details')}</Button>
        ) : null}
      </div>
    </article>
  );
}

function Row({ item, dayLabel }) {
  const { t } = useTranslation('home');
  const normalized = autoNormalizeCardData(item);
  const photo = itemPhoto(item);
  const thumb = photo ? resizeForSurface(photo, 'thumb') : null;
  const meta = [dayLabel(item), metaLine(item, t)].filter(Boolean).join(t('hero.separator'));
  const body = (
    <>
      <div className="relative w-[84px] h-16 rounded-lg overflow-hidden bg-background flex-shrink-0">
        {thumb && <img src={thumb} alt="" className="w-full h-full object-cover" loading="lazy" />}
        {/* The row is the link: the credit can't be a second control inside it. */}
        {thumb && <PhotoCredit src={thumb} size="xs" interactive={false} />}
      </div>
      <div className="min-w-0">
        <p className="text-[15px] font-semibold text-content-primary line-clamp-2">{normalized.title}</p>
        <p className="text-xs text-content-secondary truncate">{meta}</p>
      </div>
    </>
  );
  const className = 'flex items-center gap-3.5 py-3 border-t border-border first:border-t-0 can-hover:hover:bg-surface-secondary -mx-4 px-4 transition-colors duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-light';
  return <DetailLink normalized={normalized} className={className}>{body}</DetailLink>;
}

function Skeleton() {
  const { t } = useTranslation('home');
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 animate-pulse" aria-hidden="true">
      <span className="sr-only" role="status">{t('dashboardSkeleton.loadingFeed')}</span>
      <div className="md:col-span-2 bg-surface rounded-card border border-subtle overflow-hidden"><div className="aspect-[4/3] md:aspect-[2/1] bg-background" /><div className="p-4"><Shimmer className="h-4 w-2/3 bg-background rounded-lg" /></div></div>
      <div className="bg-surface rounded-card border border-subtle p-4 space-y-4">
        {Array.from({ length: 4 }).map((_, i) => <div key={i} className="flex gap-3"><Shimmer className="w-[84px] h-16 bg-background rounded-lg" /><div className="flex-1 space-y-2"><Shimmer className="h-4 w-3/4 bg-background rounded-lg" /><Shimmer className="h-3 w-1/2 bg-background rounded-lg" /></div></div>)}
      </div>
    </div>
  );
}

/**
 * "This weekend in Antwerp": what's on near home, dated things only (the
 * What's On teaser: follows lead, then the ranked nearby selection). One
 * lead card with the photo, and a list of the next few beside it.
 */
export default function ThisWeekendSection({ data, centerKind, place, awayFromHome = false, buildActions, dismissedIds, onRetry, sectionRef, headingRef }) {
  const { t } = useTranslation('home');
  const dayLabel = useDayLabel();
  const today = getLocalDateString(new Date());
  const weekendNow = new Date().getDay() >= WEEKEND_FROM_DAY || new Date().getDay() === 0;

  // The global list (no home) has no weekend to fence to.
  const { items: visible, scope } = useMemo(
    () => weekendSlice(data.items, dismissedIds, today, centerKind !== 'none', awayFromHome ? data.center : null),
    [data.items, dismissedIds, today, centerKind, awayFromHome, data.center]
  );

  const { lead, rows } = weekendShown(visible);
  const loading = (data.loading || !data.loaded) && !data.error;

  // `place` is where the list is fenced: home, or — with the phone away from
  // home — the trip under way there ("This week in Barcelona"). Unnamed, the
  // title says "near you" rather than naming a city the list isn't about.
  const titleKey = scope === 'next' ? 'titleNextWeekend' : weekendNow ? 'titleWeekend' : 'titleWeek';
  const title = centerKind === 'none'
    ? t('sections.whatsOn.globalTitle')
    : place
      ? t(`weekend.${titleKey}`, { city: place })
      : t(`weekend.${titleKey}Near`);
  const range = scope === 'next'
    ? getQuickDateRange('nextWeekend')
    : weekendNow ? getQuickDateRange('weekend') : { startDate: today, endDate: getQuickDateRange('weekend').endDate };
  const seeAll = centerKind === 'none'
    ? '/social-feed?types=events'
    : `/social-feed?types=events&where=${awayFromHome ? 'here' : 'home'}&km=50&from=${range.startDate}&to=${range.endDate}`;

  if (!loading && !data.error && visible.length === 0) return null;

  return (
    <section ref={sectionRef} aria-labelledby="home-weekend-heading" className="scroll-mt-20">
      <div ref={headingRef}>
        <HomeSectionHeader
          id="home-weekend-heading"
          title={title}
          subtitle={centerKind === 'none' ? t('weekend.subtitleGlobal') : t(awayFromHome ? 'weekend.subtitleAway' : 'weekend.subtitle')}
          action={!loading && visible.length > 0 && <SeeAllLink to={seeAll}>{t('weekend.seeAll')}</SeeAllLink>}
        />
      </div>
      {loading ? <Skeleton /> : data.error ? (
        <EmptyState.Error message={typeof data.error === 'string' ? data.error : undefined} onRetry={onRetry} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 items-start animate-fade-in">
          <div className="md:col-span-2">{lead && <LeadCard item={lead} buildActions={buildActions} dayLabel={dayLabel} />}</div>
          {rows.length > 0 && (
            <div className="bg-surface rounded-card border border-border px-4 py-1">
              {rows.map((item, i) => <Row key={`${item.content_type}-${item.id || i}`} item={item} dayLabel={dayLabel} />)}
            </div>
          )}
        </div>
      )}
      {data.followTruncated && <p className="mt-2 text-xs text-content-secondary">{t('sections.whatsOn.truncated')}</p>}
    </section>
  );
}
